import * as React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { markdownSanitizeSchema } from "./sanitizeSchema";
import { urlTransform } from "./urlTransform";
import { createCustomRenderers } from "./customRenderers";
import { splitAtArtefactLinks } from "./artefactSegments";
import { InlineArtefactViewer } from "@/components/feature/artefactsViewer/InlineArtefactViewer";
import { LetterArtefactViewer } from "@/components/feature/artefactsViewer/LetterArtefactViewer";
import { type Evidence } from "@/types/evidences";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import { isLetterArtefact } from "@/modules/chat/chat-ui.types";
import type { WebSearchSource } from "@/types/open-chat";
import type { PlacedSnapshotCitation } from "@/modules/snapshots/businessReports.types";

interface Props {
  content: string;
  evidences: Evidence[];
  processCitations: boolean;
  onCitationClick?: () => void;
  onArtefactClick?: (artefactId: string) => void;
  inlineArtefacts?: Artefact[];
  webSearchSources?: WebSearchSource[];
  onWebCitationClick?: (sourceIds: number[]) => void;
  isStreaming?: boolean;
  snapshotCitations?: PlacedSnapshotCitation[];
}

export const MarkdownRenderer: React.FC<Props> = ({
  content,
  evidences,
  processCitations,
  onCitationClick,
  onArtefactClick,
  inlineArtefacts,
  webSearchSources,
  onWebCitationClick,
  isStreaming,
  snapshotCitations,
}) => {
  const renderers = React.useMemo(
    () =>
      createCustomRenderers({
        evidences: processCitations ? evidences : [],
        onCitationClick: processCitations ? onCitationClick : undefined,
        onArtefactClick,
        inlineArtefacts,
        webSearchSources,
        onWebCitationClick,
        isStreaming,
        snapshotCitations: processCitations ? snapshotCitations : undefined,
      }),
    [
      evidences,
      onCitationClick,
      onArtefactClick,
      inlineArtefacts,
      processCitations,
      webSearchSources,
      onWebCitationClick,
      isStreaming,
      snapshotCitations,
    ],
  );

  const segments = React.useMemo(
    () => (inlineArtefacts?.length ? splitAtArtefactLinks(content) : null),
    [content, inlineArtefacts],
  );

  const markdownEl = (text: string, key?: string) => (
    <ReactMarkdown
      key={key}
      components={renderers}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
      urlTransform={urlTransform}
    >
      {text}
    </ReactMarkdown>
  );

  if (segments) {
    return (
      <div className="markdown-content relative space-y-4 break-normal">
        {segments.map((seg, i) => {
          if (seg.type === "artefact") {
            const artefact = inlineArtefacts?.find(a => a.id === seg.artefactId);
            if (!artefact) return null;
            if (isLetterArtefact(artefact)) {
              return <LetterArtefactViewer key={seg.key} artefact={artefact} onExpand={onArtefactClick} />;
            }
            return <InlineArtefactViewer key={seg.key} artefact={artefact} onExpand={onArtefactClick} />;
          }
          return markdownEl(seg.text, seg.key || `md-${i}`);
        })}
      </div>
    );
  }

  return <div className="markdown-content relative space-y-4 break-normal">{markdownEl(content)}</div>;
};
